import { useId } from 'react';
import { mergePickerItems, type PickerItem } from '@/components/modals/formPickerItems';
import React, { useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { Button } from '../../../../components/ui/Button';
import Label from '@/components/Label';
import * as Yup from 'yup';
import PickValueWithClassificationModal from '../../../../components/modals/PickValueWithClassificationModal';
import Bar from '../../../../components/elements/Bar';
import { Plus } from 'lucide-react';
import FormList from '../../../../components/elements/FormList';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const ProjectInformationModalForm = ({ visible, title, formProps, onSubmit, onClose }) => {
  const pickerSession = useId();
  const formRef = useRef<any>();
  const [visibleAddTechnicalReportItem, setVisibleAddTechnicalReportItem] = useState(false);

  const validationSchema = Yup.object().shape({
    concept_description: Yup.string().nullable().required('Povinné pole'),
    common_note: Yup.string().nullable().required('Povinné pole'),
  });

  const handleAddTechnicalReportItem = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'classified_technical_report_item_set',
      mergePickerItems(formRef.current.values.classified_technical_report_item_set, items)
    );
  };

  const handleDeleteTechnicalReportItem = (id) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.classified_technical_report_item_set.filter(
      (item) => item.id !== id
    );

    formRef.current.setFieldValue('classified_technical_report_item_set', newValues);
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            validationSchema={validationSchema}
            initialValues={{
              common_note: formProps?.common_note || null,
              concept_description: formProps?.concept_description || null,
              classified_technical_report_item_set:
                formProps?.classified_technical_report_item_set || [],
            }}
            onSubmit={(values) => {
              const reports = values.classified_technical_report_item_set.map((item) => item.id);
              onSubmit({
                ...values,
                classified_technical_report_item_set: reports,
              });
            }}
          >
            {(props) => {
              const { values, setFieldValue } = props;
              return (
                <>
                  <Form>
                    <div className="[&_ul]:list-disc [&_ul]:pl-1.5 [&_ol]:list-decimal [&_ol]:pl-1.5">
                      <Label name="concept_description" title="Popis koncepce" required />
                      <CKEditor
                        data={values.concept_description ?? ''}
                        editor={ClassicEditor}
                        config={
                          {
                            licenseKey: 'GPL',
                            list: {
                              listType: 'numbered',
                            },
                          } as any
                        }
                        onChange={(e, editor) => {
                          const data = editor.getData();
                          setFieldValue('concept_description', data);
                        }}
                      />
                    </div>

                    <div>
                      <Label name="common_note" title="Společná poznámka" required />
                      <CKEditor
                        data={values.common_note ?? ''}
                        editor={ClassicEditor}
                        config={
                          {
                            licenseKey: 'GPL',
                            list: {
                              listType: 'numbered',
                            },
                          } as any
                        }
                        onChange={(e, editor) => {
                          const data = editor.getData();
                          setFieldValue('common_note', data);
                        }}
                      />
                    </div>
                    <div className="mb-4 mt-2">
                      <Bar
                        text="Prvky technické zprávy"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            data-testid="project-info-modal-add-report-item-btn"
                            onClick={() => setVisibleAddTechnicalReportItem(true)}
                          />,
                        ]}
                      />
                      <FormList
                        data={values.classified_technical_report_item_set}
                        onDelete={handleDeleteTechnicalReportItem}
                      />
                    </div>
                    <PickValueWithClassificationModal
                      multiple
                      sessionKey={pickerSession}
                      visible={visibleAddTechnicalReportItem}
                      url="left-panel-technical-report-item/"
                      pickerUrl="technical-report-item/left-panel-picker/"
                      onClose={() => setVisibleAddTechnicalReportItem(false)}
                      onSubmit={handleAddTechnicalReportItem}
                      alreadySelectedValues={values.classified_technical_report_item_set}
                    />
                  </Form>
                </>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            data-testid="project-info-modal-close-btn"
            onClick={() => onClose()}
          >
            Zavřít
          </Button>
          <Button
            type="submit"
            key={2}
            data-testid="project-info-modal-submit-btn"
            onClick={() => formRef.current.submitForm()}
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function ProjectInformationModal(props: React.ComponentProps<typeof ProjectInformationModalForm>) {
  return props.visible ? (
    <ProjectInformationModalForm
      key={`${props.formProps?.id ?? 'create'}:${props.formProps?.id_version ?? ''}`}
      {...props}
    />
  ) : null;
}

export default ProjectInformationModal;
